import { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Fade,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

function getSubmitError(error) {
  if (error?.status === 409) {
    return "Ya existe un proveedor con ese nombre.";
  }

  if (error?.status === 400) {
    return error?.message || "Revisá los datos ingresados.";
  }

  return error?.message || "No se pudo guardar el proveedor.";
}

export default function SupplierFormModal({
  open,
  supplier = null,
  onClose,
  onSave,
}) {
  const submittingRef = useRef(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);

  const editing = Boolean(supplier?.id);

  useEffect(() => {
    if (!open) return;
    setName(supplier?.name || "");
    setPhone(supplier?.phone || "");
    setFieldErrors({});
    setSubmitError("");
    setSaving(false);
    submittingRef.current = false;
  }, [open, supplier]);

  function handleClose() {
    if (!submittingRef.current) onClose?.();
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submittingRef.current) return;

    const normalizedName = name.trim();
    const normalizedPhone = phone.trim();
    const errors = {};

    if (!normalizedName) errors.name = "Ingresá el nombre del proveedor.";
    if (normalizedName.length > 200) {
      errors.name = "El nombre puede tener hasta 200 caracteres.";
    }
    if (normalizedPhone.length > 30) {
      errors.phone = "El teléfono puede tener hasta 30 caracteres.";
    }

    setFieldErrors(errors);
    setSubmitError("");
    if (Object.keys(errors).length > 0) return;

    submittingRef.current = true;
    setSaving(true);

    try {
      await onSave?.({
        name: normalizedName,
        phone: normalizedPhone || null,
      });
    } catch (error) {
      if (error?.status === 409) {
        setFieldErrors((current) => ({
          ...current,
          name: "Ya existe un proveedor con ese nombre.",
        }));
        setSubmitError("");
      } else {
        setSubmitError(getSubmitError(error));
      }
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      PaperProps={{
        sx: {
          bgcolor: "transparent",
          boxShadow: "none",
          borderRadius: "30px",
          overflow: "visible",
          m: 1.5,
        },
      }}
      slotProps={{
        backdrop: {
          timeout: 180,
          sx: {
            bgcolor: "rgba(2, 8, 23, 0.45)",
            backdropFilter: "blur(3px)",
          },
        },
      }}
    >
      <Fade in={Boolean(open)} timeout={180}>
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit}
          sx={{
            width: "min(92vw, 520px)",
            bgcolor: "neutral.300",
            borderRadius: "30px",
            p: { xs: 2.2, sm: 3 },
            boxShadow: "0px 20px 60px rgba(0,0,0,.28)",
          }}
        >
          <Stack spacing={2.2}>
            <Stack
              direction="row"
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Box>
                <Typography
                  sx={{ color: "primary.900", fontSize: 20, fontWeight: 800 }}
                >
                  {editing ? "Editar proveedor" : "Nuevo proveedor"}
                </Typography>
                <Typography
                  sx={{ color: "neutral.700", fontSize: 12.5, fontWeight: 600 }}
                >
                  {editing
                    ? "Actualizá los datos de contacto del proveedor."
                    : "Completá los datos para agregarlo al listado."}
                </Typography>
              </Box>

              <IconButton
                type="button"
                onClick={handleClose}
                disabled={saving}
                aria-label="Cerrar modal"
              >
                <CloseIcon sx={{ color: "primary.900" }} />
              </IconButton>
            </Stack>

            <Box
              sx={{
                p: { xs: 1.8, sm: 2.2 },
                bgcolor: "neutral.50",
                borderRadius: "22px",
                boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.35)",
              }}
            >
              <Stack spacing={1.7}>
                <TextField
                  autoFocus
                  required
                  fullWidth
                  label="Nombre"
                  value={name}
                  disabled={saving}
                  error={Boolean(fieldErrors.name)}
                  helperText={fieldErrors.name || " "}
                  onChange={(event) => {
                    setName(event.target.value);
                    if (fieldErrors.name) {
                      setFieldErrors((current) => ({
                        ...current,
                        name: "",
                      }));
                    }
                  }}
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "18px",
                      bgcolor: "neutral.50",
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Teléfono"
                  value={phone}
                  disabled={saving}
                  error={Boolean(fieldErrors.phone)}
                  helperText={fieldErrors.phone || " "}
                  onChange={(event) => {
                    setPhone(event.target.value);
                    if (fieldErrors.phone) {
                      setFieldErrors((current) => ({
                        ...current,
                        phone: "",
                      }));
                    }
                  }}
                  slotProps={{ htmlInput: { maxLength: 30 } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "18px",
                      bgcolor: "neutral.50",
                    },
                  }}
                />
              </Stack>
            </Box>

            {submitError && (
              <Box
                role="alert"
                sx={{
                  px: 2,
                  py: 1.2,
                  borderRadius: "18px",
                  bgcolor: "secondary.700",
                }}
              >
                <Typography
                  sx={{ color: "logoblanco", fontSize: 13, fontWeight: 800 }}
                >
                  No se pudo guardar
                </Typography>
                <Typography
                  sx={{ color: "logoblanco", fontSize: 12, fontWeight: 600 }}
                >
                  {submitError}
                </Typography>
              </Box>
            )}

            <Stack
              direction="row"
              spacing={1.2}
              sx={{ justifyContent: "flex-end" }}
            >
              <Button
                type="button"
                onClick={handleClose}
                disabled={saving}
                sx={{
                  minWidth: 110,
                  height: 40,
                  borderRadius: "33px",
                  bgcolor: "neutral.100",
                  color: "primary.900",
                  textTransform: "none",
                  fontWeight: 800,
                  "&:hover": { bgcolor: "neutral.150" },
                }}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                disabled={saving}
                sx={{
                  minWidth: 120,
                  height: 40,
                  borderRadius: "33px",
                  bgcolor: "primary.500",
                  color: "logoblanco",
                  textTransform: "none",
                  fontWeight: 800,
                  "&:hover": { bgcolor: "primary.600" },
                  "&.Mui-disabled": {
                    bgcolor: "primary.300",
                    color: "logoblanco",
                  },
                }}
              >
                {saving ? (
                  <Stack
                    direction="row"
                    spacing={0.8}
                    sx={{ alignItems: "center" }}
                  >
                    <CircularProgress size={14} sx={{ color: "logoblanco" }} />
                    <span>Guardando...</span>
                  </Stack>
                ) : (
                  "Guardar"
                )}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Fade>
    </Dialog>
  );
}
